// Product family page: breadcrumb (Store > Category > Family), title, grid of product cards.
// Card: image (fixed size, no crop, white bg), description, buttons at bottom (Ver, Añadir a cotización).

import { useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useCategories, useProductFamilies, useSourcingItems, useBrands, useOrigins, useMoqUnits, useProductFamilyMixOptions, useProductFamilyCategories } from '@/hooks/use-store';
import { useQuotationCart } from '@/hooks/use-quotation-cart';
import { StoreBreadcrumbs } from '@/components/store/StoreBreadcrumbs';
import SEO from '@/components/SEO';
import { toAbsoluteImageUrl } from '@/lib/store-images';
import { SITE_URL, sitePath } from '@/lib/site-config';
import { StoreThumbnailImg } from '@/components/store/StoreThumbnailImg';
import { StoreCardDescription } from '@/components/store/StoreCardDescription';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { ToastAction } from '@/components/ui/toast';
import { Eye, PlusCircle, Package, MapPin, Tag, Box, Container } from 'lucide-react';
import type { SourcingItem, ProductFamily } from '@/lib/store-types';
import { AddToQuotationContainerModal } from '@/components/store/AddToQuotationContainerModal';
import type { QuotationCartLine } from '@/hooks/use-quotation-cart';

function AddToQuotationDialog({
  item,
  moqUnitLabel,
  brandName,
  originName,
  salesFormat,
  imageUrl,
  unitsPerBox,
  boxesPerContainer,
  onAdded,
}: {
  item: SourcingItem;
  moqUnitLabel: string;
  brandName?: string;
  originName?: string;
  salesFormat?: string;
  imageUrl?: string;
  unitsPerBox?: number;
  boxesPerContainer?: number;
  onAdded: () => void;
}) {
  const minMoq = Math.round(Number(item.moq_value) || 1);
  const [open, setOpen] = useState(false);
  const [quantity, setQuantity] = useState(String(Math.max(Math.ceil(minMoq), 1)));
  const { add, request } = useQuotationCart();
  const { toast } = useToast();
  const [containerModalOpen, setContainerModalOpen] = useState(false);
  const [pendingAdd, setPendingAdd] = useState<{ line: QuotationCartLine; item: SourcingItem } | null>(null);

  const handleAdd = () => {
    const q = Number(quantity);
    if (Number.isNaN(q) || q < minMoq) {
      toast({
        title: 'Cantidad inválida',
        description: `Mínimo ${minMoq} ${moqUnitLabel}`,
        variant: 'destructive',
      });
      return;
    }
    const line: QuotationCartLine = {
      sourcingItemId: item.id,
      slug: item.slug,
      name: item.internal_name,
      moqValue: minMoq,
      moqUnitId: item.moq_unit_id ?? '',
      moqUnitLabel,
      quantity: q,
      salesFormat,
      imageUrl,
      brandName,
      originName,
      unitsPerBox,
      boxesPerContainer,
    };
    setPendingAdd({ line, item });
    setContainerModalOpen(true);
  };

  return (
    <>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogTrigger asChild>
          <Button variant="outline" size="sm" className="gap-1.5 border-primary text-primary hover:bg-primary hover:text-primary-foreground">
            <PlusCircle className="h-4 w-4" />
            Añadir a cotización
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Cantidad ({moqUnitLabel})</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div>
              <Label>Cantidad (mín. {minMoq})</Label>
              <Input
                type="number"
                min={minMoq}
                value={quantity}
                onChange={(e) => setQuantity(e.target.value)}
                className="mt-1"
              />
            </div>
            <Button variant="outline" onClick={handleAdd} className="w-full border-primary text-primary hover:bg-primary hover:text-primary-foreground">Añadir a la orden</Button>
          </div>
        </DialogContent>
      </Dialog>
      {pendingAdd && (
        <AddToQuotationContainerModal
          open={containerModalOpen}
          onOpenChange={setContainerModalOpen}
          line={pendingAdd.line}
          item={pendingAdd.item}
          blocks={request?.blocks}
          add={add}
          onSuccess={() => {
            toast({
              title: 'Añadido a la orden de cotización',
              action: (
                <ToastAction asChild>
                  <Link to="/store/cart">Ver orden de cotización</Link>
                </ToastAction>
              ),
            });
            setOpen(false);
            setPendingAdd(null);
            onAdded();
          }}
          onError={(msg) => toast({ title: msg ?? 'Error', description: 'No se pudo añadir', variant: 'destructive' })}
        />
      )}
    </>
  );
}

const StoreFamily = () => {
  const { categorySlug, familySlug } = useParams<{ categorySlug: string; familySlug: string }>();
  const { data: categories, isLoading: categoriesLoading } = useCategories();
  const category = categories?.find((c) => c.slug === categorySlug);
  const categoryId = category?.id;
  // Wait for categoryId before fetching families; avoids race with "all families"
  const { data: families, isLoading: familiesLoading } = useProductFamilies(
    categoryId ?? null,
    { enabled: !!categoryId }
  );
  const family = families?.find((f) => f.slug === familySlug);
  const { data: mixOptionsRows } = useProductFamilyMixOptions();
  const { data: familyCategories } = useProductFamilyCategories();
  const { data: allFamilies } = useProductFamilies(null, { enabled: !!family?.id && (mixOptionsRows?.length ?? 0) > 0 });
  const relatedFamilyIds = useMemo(() => {
    if (!family?.id || !mixOptionsRows?.length) return [];
    const set = new Set<string>();
    mixOptionsRows.forEach((r) => {
      if (r.family_id === family.id) set.add(r.related_family_id);
      if (r.related_family_id === family.id) set.add(r.family_id);
    });
    return Array.from(set);
  }, [family?.id, mixOptionsRows]);
  const relatedFamiliesWithCategory = useMemo(() => {
    if (!allFamilies?.length || !familyCategories?.length || !categories?.length || relatedFamilyIds.length === 0) return [];
    const categoryBySlug = new Map(categories.map((c) => [c.id, c.slug]));
    const firstCategoryByFamily = new Map<string, string>();
    familyCategories.forEach((r) => {
      if (!firstCategoryByFamily.has(r.family_id)) firstCategoryByFamily.set(r.family_id, r.category_id);
    });
    return relatedFamilyIds
      .map((fid) => {
        const fam = allFamilies.find((f) => f.id === fid);
        if (!fam) return null;
        const catId = firstCategoryByFamily.get(fid);
        const categorySlug = catId ? categoryBySlug.get(catId) : undefined;
        return { family: fam, categorySlug: categorySlug ?? '' };
      })
      .filter((x): x is { family: ProductFamily; categorySlug: string } => x != null && x.categorySlug !== '');
  }, [allFamilies, familyCategories, categories, relatedFamilyIds]);
  const hasMixOptions = relatedFamiliesWithCategory.length > 0;
  // Only fetch items when family is resolved; prevents requesting "all items" while family is still loading
  const { data: items, isLoading: itemsLoading } = useSourcingItems(family?.id ?? null, { enabled: !!family?.id });
  const { data: brands } = useBrands();
  const { data: origins } = useOrigins();
  const { data: moqUnits } = useMoqUnits();

  const breadcrumbs = [
    { label: 'Store', path: '/store' },
    { label: category?.name ?? categorySlug ?? 'Category', path: categorySlug ? `/store/category/${categorySlug}` : undefined },
    { label: family?.family_name ?? familySlug ?? 'Family' },
  ];

  const seoBreadcrumbs: Array<{ name: string; url: string }> = [
    { name: 'Store', url: sitePath('/store') },
    ...(category && categorySlug ? [{ name: category.name, url: sitePath(`/store/category/${categorySlug}`) }] : []),
    ...(family && categorySlug && familySlug ? [{ name: family.family_name, url: sitePath(`/store/category/${categorySlug}/${familySlug}`) }] : []),
  ];

  const familyDescription =
    family && (family.marketing_description?.trim() || `${family.family_name} - Productos en Padmora B2B`);
  const familyStructuredData =
    family && categorySlug && familySlug
      ? {
          '@context': 'https://schema.org',
          '@graph': [
            { '@type': 'Organization', name: 'Padmora', url: SITE_URL, logo: sitePath('/android-chrome-512x512.png') },
            {
              '@type': 'CollectionPage',
              name: family.family_name,
              description: familyDescription,
              url: sitePath(`/store/category/${categorySlug}/${familySlug}`),
            },
          ],
        }
      : undefined;

  return (
    <div className="space-y-6">
      {family && category && categorySlug && familySlug && (
        <SEO
          title={`${family.family_name} | Padmora B2B`}
          description={
            family.marketing_description?.trim() ||
            `${family.family_name} - Productos en Padmora B2B`
          }
          image={toAbsoluteImageUrl(family.showroom_image ?? family.thumbnail) ?? undefined}
          canonical={sitePath(`/store/category/${categorySlug}/${familySlug}`)}
          breadcrumbs={seoBreadcrumbs}
          structuredData={familyStructuredData}
        />
      )}
      <StoreBreadcrumbs segments={breadcrumbs} />
      <div>
        <h1 className="text-3xl font-bold text-zinc-900 tracking-tight">{family?.family_name ?? familySlug}</h1>
        {family?.marketing_description && (
          <p className="mt-2 text-zinc-600 max-w-2xl">{family.marketing_description}</p>
        )}
      </div>
      {categoriesLoading || familiesLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="overflow-hidden border-zinc-200">
              <div className="p-4 flex flex-col items-center gap-3">
                <Skeleton className="h-28 w-32 rounded-lg" />
                <div className="w-full space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : !family ? (
        <p className="text-zinc-600">No se encontró la familia.</p>
      ) : itemsLoading ? (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="overflow-hidden border-zinc-200">
              <div className="p-4 flex flex-col items-center gap-3">
                <Skeleton className="h-28 w-32 rounded-lg" />
                <div className="w-full space-y-2">
                  <Skeleton className="h-5 w-3/4" />
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-1/2" />
                </div>
              </div>
            </Card>
          ))}
        </div>
      ) : (
        <div className={`flex flex-col gap-6 ${hasMixOptions ? 'lg:flex-row lg:items-start' : ''}`}>
          <div key={family.id} className={`grid gap-4 sm:grid-cols-2 lg:grid-cols-3 min-w-0 flex-1 ${hasMixOptions ? 'xl:grid-cols-4' : 'xl:grid-cols-4'}`}>
          {(items ?? []).map((item) => {
            const brand = brands?.find((b) => b.id === item.brand_id);
            const origin = origins?.find((o) => o.id === item.origin_id);
            const moqUnit = moqUnits?.find((u) => u.id === item.moq_unit_id);
            const moqUnitLabel = moqUnit?.name ?? '';

            return (
              <Card
                key={item.id}
                className="overflow-hidden border-zinc-200 bg-white flex flex-col shadow-sm transition-all hover:shadow-md hover:-translate-y-0.5"
              >
                {/* Header: Image + Title + Format */}
                <CardHeader className="p-4 pb-3 border-b border-zinc-100">
                  <div className="flex flex-row items-center gap-3">
                    {/* Product image: clickable, goes to product detail */}
                    <Link
                      to={`/store/product/${item.slug}`}
                      className="shrink-0 w-28 h-24 flex items-center justify-center rounded-lg border border-zinc-200 bg-white overflow-hidden hover:border-primary/50 hover:ring-2 hover:ring-primary/20 transition-all focus:outline-none focus:ring-2 focus:ring-primary/30"
                    >
                      {item.packshot_image ? (
                        <StoreThumbnailImg
                          coverUrl={item.packshot_image}
                          size={{ width: 112, height: 96 }}
                          className="max-h-full max-w-full w-auto h-auto object-contain"
                        />
                      ) : (
                        <span className="text-zinc-400 text-xs">Sin imagen</span>
                      )}
                    </Link>
                    <div className="flex-1 min-w-0 flex flex-col justify-center">
                      <h2 className="font-semibold text-zinc-900 text-base leading-tight line-clamp-2 mb-1">
                        {item.internal_name}
                      </h2>
                      {item.sales_format && (
                        <StoreCardDescription className="text-xs text-zinc-600">
                          {item.sales_format}
                        </StoreCardDescription>
                      )}
                    </div>
                  </div>
                </CardHeader>

                {/* Information Section: clear structure with icons and Spanish labels */}
                <CardContent className="p-4 pt-3 flex-1 space-y-2.5 text-xs">
                  {/* Pedido Mínimo (MOQ) - same row style as other data */}
                  {item.moq_value != null && (
                    <div className="flex items-center gap-2 text-zinc-600">
                      <Package className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                      <span>
                        <span className="text-zinc-500">Pedido Mínimo:</span>{' '}
                        <span className="text-zinc-800 font-semibold">{Math.round(Number(item.moq_value) || 0).toLocaleString()} {moqUnitLabel}</span>
                      </span>
                    </div>
                  )}

                  {/* Brand */}
                  {brand && (
                    <div className="flex items-center gap-2 text-zinc-600">
                      <Tag className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                      <span>
                        <span className="text-zinc-500">Marca:</span>{' '}
                        <span className="text-zinc-800 font-medium">{brand.name}</span>
                      </span>
                    </div>
                  )}

                  {/* Origin */}
                  {origin && (
                    <div className="flex items-center gap-2 text-zinc-600">
                      <MapPin className="h-3.5 w-3.5 shrink-0 text-zinc-400" />
                      <span>
                        <span className="text-zinc-500">Origen:</span>{' '}
                        <span className="text-zinc-800 font-medium">{origin.name}</span>
                      </span>
                    </div>
                  )}

                  {/* Logistics: units per box and per container */}
                  {(item.units_per_box != null && item.units_per_box > 0) || (item.boxes_per_container != null && item.boxes_per_container > 0) ? (
                    <div className="flex items-start gap-2 text-zinc-600">
                      <Box className="h-3.5 w-3.5 shrink-0 text-zinc-400 mt-0.5" />
                      <div className="space-y-0.5">
                        {item.units_per_box != null && item.units_per_box > 0 && (
                          <p>
                            <span className="text-zinc-500">Uds/caja:</span>{' '}
                            <span className="text-zinc-800 font-medium">{item.units_per_box}</span>
                          </p>
                        )}
                        {item.boxes_per_container != null && item.boxes_per_container > 0 && (
                          <p>
                            <span className="text-zinc-500">Cajas/contenedor:</span>{' '}
                            <span className="text-zinc-800 font-medium">{item.boxes_per_container.toLocaleString()}</span>
                          </p>
                        )}
                      </div>
                    </div>
                  ) : null}
                </CardContent>

                {/* Action buttons */}
                <div className="p-4 pt-3 flex gap-2 justify-start flex-wrap border-t border-zinc-100 bg-zinc-50/60">
                  <Button variant="outline" size="sm" asChild className="gap-1.5">
                    <Link to={`/store/product/${item.slug}`}>
                      <Eye className="h-4 w-4" />
                      Ver detalles
                    </Link>
                  </Button>
                  <AddToQuotationDialog
                    item={item}
                    moqUnitLabel={moqUnitLabel}
                    brandName={brand?.name}
                    originName={origin?.name}
                    salesFormat={item.sales_format}
                    imageUrl={item.packshot_image}
                    unitsPerBox={item.units_per_box ?? undefined}
                    boxesPerContainer={item.boxes_per_container ?? undefined}
                    onAdded={() => {}}
                  />
                </div>
              </Card>
            );
          })}
          </div>
          {hasMixOptions && (
            <aside className="lg:w-72 lg:min-w-[18rem] lg:shrink-0 rounded-xl border border-primary/20 bg-primary/5 p-4">
              <h3 className="font-semibold text-sm text-zinc-900 flex items-center gap-2">
                <Container className="h-4 w-4 text-primary shrink-0" />
                Se pueden mezclar en un mismo contenedor
              </h3>
              <p className="text-xs text-zinc-600 mt-1.5">
                Estos productos y los de las familias siguientes se pueden combinar en un mismo contenedor en tu cotización.
              </p>
              <ul className="mt-3 space-y-2">
                {relatedFamiliesWithCategory.map(({ family: relFamily, categorySlug: relCatSlug }) => (
                  <li key={relFamily.id}>
                    <Link
                      to={`/store/category/${relCatSlug}/${relFamily.slug}`}
                      className="flex items-center gap-3 rounded-lg border border-transparent bg-white/80 p-2.5 shadow-sm transition hover:border-primary/30 hover:bg-white hover:shadow"
                    >
                      <div className="h-14 w-16 shrink-0 overflow-hidden rounded-md border border-zinc-200 bg-zinc-100 flex items-center justify-center">
                        {relFamily.showroom_image ? (
                          <StoreThumbnailImg
                            coverUrl={relFamily.showroom_image}
                            size={{ width: 64, height: 56 }}
                            className="h-full w-full object-cover"
                            alt=""
                          />
                        ) : (
                          <Box className="h-6 w-6 text-zinc-400" />
                        )}
                      </div>
                      <span className="text-sm font-medium text-zinc-900 line-clamp-2 min-w-0">
                        {relFamily.family_name}
                      </span>
                    </Link>
                  </li>
                ))}
              </ul>
            </aside>
          )}
        </div>
      )}
    </div>
  );
};

export default StoreFamily;
